import { getStops } from "../../../app/api/getStops";
import { postCustomBookingsSearch } from "../../../app/api/postCustomBookingsSearch";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { getUserProfile } from "../../../Lib/Utils/SessionStorageUtil";
import stopManager from "../../../Lib/Utils/StopManager";
import { formatDayAndMonthByLocale, getShortDateFromDateStringFormatted } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import type { IBookingsListPagination } from "../../../Orchestrator/Recoils/BookingAtom";
import { IBookedTrips, IBookingSearchResult, IBookingsSearchRequest, IMultiTripsBookingSummary } from "../../../Orchestrator/Types/Index";
import { AdmissionStatusEnum, BookingPartTypeEnum, TripDirectionTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ITabChangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/UpcomingPastBookingActionParams";
import { getBookingToRightDataAction } from "./GetBookingToRightDataAction";
import { isFullyCancelledBooking, sortBookingSummariesForDisplay } from "./bookingDisplayPagination";
import { bookingsPastPivot, loadBookingAppearanceMinutes } from "../../../Lib/Utils/bookingAppearancePeriod";

const MULTI_BOOKING_TAG = "MultiBooking";

const hasMultiBookingTag = (booking: IBookingSearchResult): boolean => {
    const fromSummaries = (booking.bookedOfferSummaries || []).some((summary) =>
        (summary.throughTicketTags || []).includes(MULTI_BOOKING_TAG)
    );
    if (fromSummaries) {
        return true;
    }

    const bookedOffers = (booking as unknown as { bookedOffers?: { appliedThroughTicketTags?: string[] }[] }).bookedOffers || [];
    return bookedOffers.some((offer) =>
        (offer?.appliedThroughTicketTags || []).includes(MULTI_BOOKING_TAG)
    );
};

const splitBookingsByMultiTag = (bookings: IBookingSearchResult[]) => {
    return bookings.reduce(
        (acc, booking) => {
            if (hasMultiBookingTag(booking)) {
                acc.multi.push(booking);
            } else {
                acc.regular.push(booking);
            }
            return acc;
        },
        { multi: [] as IBookingSearchResult[], regular: [] as IBookingSearchResult[] }
    );
};

// Exported for tests: this derives the status that `transformMultiBookingTrips`
// filters on, so it decides multi-trip list membership on both tabs.
//
// Reads ADMISSION parts only, for the same reason `isFullyCancelledBooking`
// does: `bookingPartSummaries` also carries Ancillary and Reservation parts
// with independent statuses, and the badge is derived from admissions alone.
// Counting them here would let a lone FULFILLED ancillary label a cancelled
// booking "Confirmed" while the sort sinks it as cancelled.
export const determineBookingStatusMeta = (booking: IBookingSearchResult) => {
    const allSummaries = booking.bookedOfferSummaries || [];
    const fulfilledSummaries = allSummaries.filter((summary) =>
        (summary.bookingPartSummaries || []).some(
            (part) =>
                part.type === BookingPartTypeEnum.ADMISSION &&
                part.status === AdmissionStatusEnum.FULFILLED
        )
    );

    // Fallback reads ADMISSIONS across all offers, not blindly part [0] of
    // offer [0]: a non-Admission part landing first would report a status the
    // badge never derives. REFUNDED/RELEASED admissions are skipped because
    // the branch above owns that state — reaching here means some admission is
    // NOT refunded, so reporting a refunded one would label the card
    // "Cancelled" while a live admission remains. Skipping them also makes the
    // result independent of the order the backend lists offers in.
    const firstLiveAdmissionStatus =
        allSummaries
            .flatMap((summary) => summary.bookingPartSummaries || [])
            .find(
                (part) =>
                    part.type === BookingPartTypeEnum.ADMISSION &&
                    part.status !== AdmissionStatusEnum.REFUNDED &&
                    part.status !== AdmissionStatusEnum.RELEASED
            )?.status || "";

    const bookingStatus =
        fulfilledSummaries.length > 0
            ? AdmissionStatusEnum.FULFILLED
            : isFullyCancelledBooking(booking)
                ? AdmissionStatusEnum.REFUNDED
                : firstLiveAdmissionStatus;

    return { bookingStatus };
};

const selectReferenceTrip = (tripSummaries: IBookingSearchResult["tripSummaries"] = [], preferUpcoming: boolean) => {
    if (!tripSummaries || tripSummaries.length === 0) return null;
    const sortedTrips = [...tripSummaries].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    const now = new Date();

    if (preferUpcoming) {
        return (
            sortedTrips.find((trip) => new Date(trip.startTime).getTime() >= now.getTime()) ||
            sortedTrips[0]
        );
    }

    return sortedTrips[sortedTrips.length - 1];
};

const formatDisplayDate = (isoDate?: string) =>
    isoDate ? formatDayAndMonthByLocale(isoDate) : "";

const transformMultiBookingTrips = (
    bookings: IBookingSearchResult[],
    preferUpcoming: boolean
): IMultiTripsBookingSummary[] => {
    return bookings
        .map((booking) => {
            const referenceTrip = selectReferenceTrip(booking.tripSummaries, preferUpcoming);
            if (!referenceTrip) return null;

            const { bookingStatus } = determineBookingStatusMeta(booking);
            if (bookingStatus !== "FULFILLED" && bookingStatus !== "REFUNDED") {
                return null;
            }

            const allTrips = booking.tripSummaries || [];
            const tripCount = allTrips.length;
            const hasOutboundTrip = allTrips.some(
                (trip) => trip.direction === TripDirectionTypeEnum.OUTBOUND
            );
            const hasInboundTrip = allTrips.some(
                (trip) => trip.direction === TripDirectionTypeEnum.INBOUND
            );
            const isRoundTrip = hasOutboundTrip && hasInboundTrip;
            const sortedTrips = [...allTrips].sort(
                (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
            );
            const earliestTrip = sortedTrips[0];
            const latestTrip = sortedTrips[sortedTrips.length - 1] || earliestTrip;

            const startDateLabel = formatDisplayDate(earliestTrip?.startTime);
            const endDateLabel = formatDisplayDate(latestTrip?.endTime || latestTrip?.startTime);

            const fromRef = referenceTrip.origin?.stopPlaceRef || "";
            const toRef = referenceTrip.destination?.stopPlaceRef || "";

            const fromName = stopManager.getStopNameById(fromRef);
            const toName = stopManager.getStopNameById(toRef);

            return {
                bookingId: booking.id,
                bookingCode: booking.code,
                fromName,
                toName,
                status: bookingStatus,
                tripCount,
                startDateLabel,
                endDateLabel,
                isRoundTrip,
            };
        })
        .filter((trip): trip is IMultiTripsBookingSummary => Boolean(trip));
};

export const BOOKINGS_PAGE_SIZE = 5;

// The backend's `?offset=` is broken with `travelDateRange`, so we can't page
// the search server-side. Instead we fetch all the (lightweight) summaries for
// the window in one call, sort them by date client-side, then build the
// expensive per-booking details a page at a time ("Load more"). This constant
// is the ceiling on summaries fetched for one tab/month window.
export const BOOKINGS_MAX_FETCH = 100;

// The fetch is capped at BOOKINGS_MAX_FETCH and the backend does not return
// results in any guaranteed (date) order, so hitting the cap means an arbitrary
// subset is missing — not just "the oldest". Surface it so it's diagnosable.
export function warnIfBookingsTruncated(resultCount: number): void {
    if (resultCount >= BOOKINGS_MAX_FETCH) {
        console.warn(
            `[bookings] result set hit the ${BOOKINGS_MAX_FETCH}-booking fetch cap; some bookings may not be shown.`
        );
    }
}

export async function processBookingsList(
    bookings: IBookingSearchResult[],
    activeTab: "1" | "2",
    isAnonymous: boolean,
    options: { append?: boolean } = {}
) {
    const { append = false } = options;
    const { multi, regular } = splitBookingsByMultiTag(bookings);
    let multiTrips: IMultiTripsBookingSummary[] = [];

    if (multi.length > 0) {
        await getStops();
        multiTrips = transformMultiBookingTrips(multi, activeTab === "1");
    }

    // Multi-trip atoms: replace on first page, append on subsequent pages.
    if (append) {
        if (multiTrips.length > 0) {
            const targetKey = activeTab === "1" ? "upcomingMultiBookingTripsAtom" : "pastMultiBookingTripsAtom";
            const existing = (getRecoilStateValue<IMultiTripsBookingSummary[]>(targetKey) as IMultiTripsBookingSummary[] | undefined) || [];
            const seen = new Set(existing.map((t) => t.bookingId));
            const merged = [...existing, ...multiTrips.filter((t) => !seen.has(t.bookingId))];
            updateRecoilState(targetKey, merged);
        }
    } else {
        if (activeTab === "1") {
            updateRecoilState("upcomingMultiBookingTripsAtom", multiTrips);
            updateRecoilState("pastMultiBookingTripsAtom", []);
        } else {
            updateRecoilState("pastMultiBookingTripsAtom", multiTrips);
            updateRecoilState("upcomingMultiBookingTripsAtom", []);
        }
    }

    if (regular.length > 0) {
        getBookingToRightDataAction.setParams({
            bookedTrips: regular,
            activeTab,
            isAnonymous,
            append,
        });
        await getBookingToRightDataAction.execute();
    } else if (!append) {
        updateRecoilState('bookedTripsAtom', []);
        updateRecoilState('bookedNextTripsAtom', null);
        if (activeTab === "1") {
            const shouldShowRetrieve: boolean = multiTrips.length === 0;
            updateRecoilState('showRetrieveBookingAtom', shouldShowRetrieve);
        }
    }
}

class UpcomingPastBookingTabAction extends BaseAction<void, ITabChangeActionParams> {

    private async processBookingsList(
        bookings: IBookingSearchResult[],
        activeTab: "1" | "2",
        isAnonymous: boolean
    ) {
        await processBookingsList(bookings, activeTab, isAnonymous);
    }

    async getLastPassingMonthStartAndEndDate(month: number, isAnonymous: boolean = false, retrieveBookingsIds: string[]) {
        try {
            //Clear existing search train
            updateRecoilState('bookedTripsAtom', []);

            // Set Month To past booking filter
            updateRecoilState("lastMonthAtom", month)

            const currentDateTime = new Date();
            currentDateTime.setUTCHours(currentDateTime.getUTCHours() + 4);

            // Past starts where Upcoming ends: the appearance period, not now.
            // Both ranges pivot on the same instant or a just-departed booking
            // is returned by both searches, or by neither.
            await loadBookingAppearanceMinutes();
            currentDateTime.setTime(bookingsPastPivot(currentDateTime).getTime());

            // Current date with current time for endDate
            currentDateTime.setSeconds(currentDateTime.getSeconds() - 1)
            const endDate = currentDateTime.toISOString()

            // Start date three months back with start of the day time
            const currentDateTimeForMonth = new Date();
            currentDateTimeForMonth.setUTCHours(currentDateTimeForMonth.getUTCHours() + 4);
            currentDateTimeForMonth.setMonth(currentDateTimeForMonth.getMonth() - month);

            updateRecoilState('bookedTripsAtom', []);
            updateRecoilState('bookedNextTripsAtom', null);

            const session = await getUserProfile();
            const searchRequest: IBookingsSearchRequest = {
                travelDateRange: {
                    startTime: currentDateTimeForMonth.toISOString(),
                    endDate: endDate
                },
                purchaserPassengerEmail: session?.email || ""
            };
            const response: any = await postCustomBookingsSearch(searchRequest, BOOKINGS_MAX_FETCH, 0);

            const allResultsRaw: IBookingSearchResult[] =
                response?.status === 'ok' && response.answer
                    ? (response?.answer?.bookingSearchResults || [])
                    : [];
            warnIfBookingsTruncated(allResultsRaw.length);
            // Past = lazy: sort summaries up front, build only the first page's
            // details now; "Load more" builds the next page (see LoadMoreBookingsAction).
            const allResults = sortBookingSummariesForDisplay(allResultsRaw, "2");
            const firstPage = allResults.slice(0, BOOKINGS_PAGE_SIZE);

            updateRecoilState<IBookingsListPagination>("bookingsListPaginationAtom", {
                activeTab: "2",
                offset: firstPage.length,
                pageSize: BOOKINGS_PAGE_SIZE,
                allResults,
                isLoadingMore: false,
                lastSearchRequest: searchRequest,
                lastIsAnonymous: isAnonymous,
                lastRetrieveBookingsIds: retrieveBookingsIds || [],
            });

            await this.processBookingsList(firstPage, "2", isAnonymous);
        } catch (error: any) {
            console.error("Error fetching data:", error);
        } finally {
            _eventBus.emit({
                eventName: "BOOKING_DATA_LOADED",
            });
        }
    }

    override async run(): Promise<void> {
        try {
            const { activeTab, isAnonymous, retrieveBookingsIds } = this.params!
            if (activeTab == "2") {
                await this.getLastPassingMonthStartAndEndDate(3, isAnonymous, retrieveBookingsIds || []);
            } else {
                const currentDateTime = new Date();
                currentDateTime.setUTCHours(currentDateTime.getUTCHours() + 4);

                // Start the Upcoming window one appearance period BEFORE now, so a
                // train that has just departed is still returned: the passenger on
                // board is exactly who needs to open this ticket.
                await loadBookingAppearanceMinutes();
                currentDateTime.setTime(bookingsPastPivot(currentDateTime).getTime());
                const dubaiCurrentDateTimeUTC = currentDateTime.toISOString();

                currentDateTime.setUTCFullYear(currentDateTime.getFullYear() + 1);
                const dubaiOneYearDateTimeUTC = currentDateTime.toISOString();

                updateRecoilState("isPastBookingDetailsAtom", false);
                updateRecoilState('bookedTripsAtom', []);
                updateRecoilState('bookedNextTripsAtom', null);

                const session = await getUserProfile();
                const searchRequest: IBookingsSearchRequest = {
                    travelDateRange: {
                        startTime: dubaiCurrentDateTimeUTC,
                        endDate: dubaiOneYearDateTimeUTC
                    },
                    purchaserPassengerEmail: session?.email || ""
                };

                const response: any = await postCustomBookingsSearch(searchRequest, BOOKINGS_MAX_FETCH, 0);

                const allResultsRaw: IBookingSearchResult[] =
                    response?.status === 'ok' && response.answer
                        ? (response?.answer?.bookingSearchResults || [])
                        : [];
                warnIfBookingsTruncated(allResultsRaw.length);
                // Upcoming = lazy: sort summaries soonest-first and build only the
                // first page's details now; "Load more" builds the next page. The
                // next-ticket is the soonest, which sorts into page 1.
                const allResults = sortBookingSummariesForDisplay(allResultsRaw, "1");
                const firstPage = allResults.slice(0, BOOKINGS_PAGE_SIZE);

                updateRecoilState<IBookingsListPagination>("bookingsListPaginationAtom", {
                    activeTab: "1",
                    offset: firstPage.length,
                    pageSize: BOOKINGS_PAGE_SIZE,
                    allResults,
                    isLoadingMore: false,
                    lastSearchRequest: searchRequest,
                    lastIsAnonymous: !!isAnonymous,
                    lastRetrieveBookingsIds: retrieveBookingsIds || [],
                });

                await this.processBookingsList(firstPage, activeTab as "1" | "2", !!isAnonymous);
            }

            updateRecoilState("activeBookingTabAtom", activeTab);

            if (activeTab == "2") {
                updateRecoilState("showRetrieveBookingAtom", false);
            }
        } catch (error: any) {
            console.error("Error fetching data:", error);
        } finally {
            _eventBus.emit({
                eventName: "BOOKING_DATA_LOADED"
            });
        }
    }
}

export const upcomingPastBookingTabAction = new UpcomingPastBookingTabAction()